/**
 * ATLAS voice clip manifest — ONE module, no per-file pointer imports.
 *
 * Previously each clip was a separate `*.mp3.asset.json` import (25 modules),
 * which cost 25 extra module requests on the ATLAS page and competed with the
 * transcription request. The CDN URLs are content-addressed and immutable, so
 * they are inlined here as plain strings.
 *
 * To add clips: upload to src/assets/atlas-voice/ and paste the new `url`
 * from the generated `.asset.json` into the matching array below.
 */
export type AtlasResponseCategory =
  | "quoteSubmitted"
  | "quoteError"
  | "loadPosted"
  | "loadError"
  | "timeout";

export const ATLAS_RESPONSE_CLIPS: Record<AtlasResponseCategory, string[]> = {
  quoteSubmitted: [
    "/__l5e/assets-v1/74681052-ab97-4907-92a9-a96a12ea430f/quote-submitted1.mp3",
    "/__l5e/assets-v1/84fde91c-7b3a-43a5-8702-4eba2ae9e2e6/quote-submitted2.mp3",
    "/__l5e/assets-v1/c668099d-a44c-425c-abee-7ded0251087a/quote-submitted3.mp3",
    "/__l5e/assets-v1/181c605a-95e6-44a3-b770-4df2e9ff6eb1/quote-submitted4.mp3",
    "/__l5e/assets-v1/ad3280d8-2d3e-42c7-89aa-f6c542db7c58/quote-submitted5.mp3",
  ],
  quoteError: [
    "/__l5e/assets-v1/20ba1e43-8f05-4806-8760-ce0e8f760397/quote-error1.mp3",
    "/__l5e/assets-v1/7c24e1dd-0e91-4ac6-bc95-62b5fc67ed38/quote-error2.mp3",
    "/__l5e/assets-v1/2421688b-f446-4336-aadb-55be84bacfc7/quote-error3.mp3",
    "/__l5e/assets-v1/1e22124e-7f00-4700-aa81-9d5ad9283311/quote-error4.mp3",
    "/__l5e/assets-v1/9d497789-bda0-46e9-89f2-667b7aa37100/quote-error5.mp3",
  ],
  loadPosted: [
    "/__l5e/assets-v1/5e636cfa-7b57-4cf6-8ca5-c867d4783ae1/load-posted1.mp3",
    "/__l5e/assets-v1/4cb15e17-b78c-4a57-8561-03a8b4220dd2/load-posted2.mp3",
    "/__l5e/assets-v1/ee789a5d-f3fd-47ac-a60b-2692e6ee0fec/load-posted3.mp3",
    "/__l5e/assets-v1/60aa1d57-48e2-4748-8fe2-77be98a49bb2/load-posted4.mp3",
    "/__l5e/assets-v1/fd721fa1-0d41-476b-af6b-01abdf7789bc/load-posted5.mp3",
  ],
  loadError: [
    "/__l5e/assets-v1/7c06b25a-542c-44ed-9f87-68bfc06a0cba/load-error1.mp3",
    "/__l5e/assets-v1/a4dbb075-5d04-4256-bdf9-7657d3f669ff/load-error2.mp3",
    "/__l5e/assets-v1/358c689c-6186-4d48-ae0d-d8fa5cb0039d/load-error3.mp3",
    "/__l5e/assets-v1/f7bd80d5-23cf-4c50-8631-2b4d7364f311/load-error4.mp3",
    "/__l5e/assets-v1/cbcc6ec2-5eac-46f5-a57d-7cccbd7c5543/load-error5.mp3",
  ],
  timeout: [
    "/__l5e/assets-v1/e0ebc9fd-4f25-44dc-bddc-f791f3d361e2/timeout1.mp3",
    "/__l5e/assets-v1/a8d31e43-915a-4ae6-be02-0b8d2aa6ca56/timeout2.mp3",
    "/__l5e/assets-v1/829a629d-22fe-43d7-912d-8685fff00268/timeout3.mp3",
    "/__l5e/assets-v1/5b12ac03-bd09-4572-a62d-4d82c94d3dea/timeout4.mp3",
    "/__l5e/assets-v1/f07307e5-265b-49bb-acc2-8cd29741a3d0/timeout5.mp3",
  ],
};

/** Random clip for a category. Empty category → null (silent no-op). */
export function getRandomAtlasResponseUrl(
  category: AtlasResponseCategory,
): string | null {
  const list = ATLAS_RESPONSE_CLIPS[category] || [];
  if (list.length === 0) return null;
  return list[Math.floor(Math.random() * list.length)] || null;
}

/** First known clip URL — used only to prime the audio element on a gesture. */
export function getAnyAtlasClipUrl(): string | null {
  for (const list of Object.values(ATLAS_RESPONSE_CLIPS)) {
    if (list.length > 0) return list[0];
  }
  return null;
}
